import { EditorView } from '@codemirror/view';

/**
 * Service to handle auto-scrolling to the first occurrence of a selected term
 * in both claims and spec editors
 */
export class TermAutoScrollService {
    constructor() {
        this.claimsEditor = null;
        this.specEditor = null;
    }

    /**
     * Set the claims editor reference
     * @param {EditorView} editor - CodeMirror editor view for claims
     */
    setClaimsEditor(editor) {
        this.claimsEditor = editor;
    }

    /**
     * Set the spec editor reference
     * @param {Editor} editor - TipTap editor for spec
     */
    setSpecEditor(editor) {
        this.specEditor = editor;
    }

    /**
     * Scroll to the first occurrence of a term in the claims editor
     * @param {Object} termData - The term data from termsStore
     * @returns {boolean} - true if scrolled, false if not found
     */
    scrollToTermInClaims(termData) {
        if (!this.claimsEditor || !termData) return false;

        try {
            // Try to use absolute position first
            if (termData.abs_start_char !== undefined && termData.abs_end_char !== undefined) {
                const pos = termData.abs_start_char;
                
                // Scroll to position
                this.claimsEditor.dispatch({
                    effects: EditorView.scrollIntoView(pos, {
                        y: 'center',
                        yMargin: 100
                    })
                });
                
                
                console.log(`[TermAutoScroll] Scrolled to term "${termData.parent_term}" at position ${pos} in claims`);
                return true;
            }
            
            // Fallback to searching for the term
            const text = this.claimsEditor.state.doc.toString();
            const termRegex = new RegExp(`\\b${termData.parent_term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
            const match = termRegex.exec(text);
            
            if (match) {
                const pos = match.index;
                
                // Scroll to position
                this.claimsEditor.dispatch({
                    effects: EditorView.scrollIntoView(pos, {
                        y: 'center',
                        yMargin: 100
                    })
                });
                
                
                console.log(`[TermAutoScroll] Scrolled to term "${termData.parent_term}" at position ${pos} in claims (fallback)`);
                return true;
            }
        } catch (error) {
            console.error('[TermAutoScroll] Error scrolling to term in claims:', error);
        }
        
        return false;
    }

    /**
     * Scroll to the first occurrence of a term in the spec editor
     * @param {Object} termData - The term data from termsStore
     * @returns {boolean} - true if scrolled, false if not found
     */
    scrollToTermInSpec(termData) {
        if (!this.specEditor || !termData) return false;

        try {
            const term = termData.parent_term;
            const doc = this.specEditor.state.doc;
            let firstPosition = null;
            
            // Search for the term in the document
            doc.descendants((node, pos) => {
                if (node.isText && !firstPosition) {
                    const termRegex = new RegExp(`\\b${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
                    const match = termRegex.exec(node.text);
                    
                    if (match) {
                        firstPosition = pos + match.index;
                        return false; // Stop searching
                    }
                }
            });
            
            if (firstPosition !== null) {
                // Focus the editor
                this.specEditor.commands.focus();
                
                // Set selection to the term position
                this.specEditor.commands.setTextSelection(firstPosition);
                
                // Scroll into view with animation
                const domAtPos = this.specEditor.view.domAtPos(firstPosition);
                if (domAtPos && domAtPos.node) {
                    const element = domAtPos.node.nodeType === Node.TEXT_NODE 
                        ? domAtPos.node.parentElement 
                        : domAtPos.node;
                    
                    if (element) {
                        element.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center',
                            inline: 'nearest'
                        });
                        
                    }
                }
                
                console.log(`[TermAutoScroll] Scrolled to term "${term}" at position ${firstPosition} in spec`);
                return true;
            }
        } catch (error) {
            console.error('[TermAutoScroll] Error scrolling to term in spec:', error);
        }
        
        return false;
    }


    /**
     * Scroll to term in both editors
     * @param {Object} termData - The term data from termsStore
     */
    scrollToTerm(termData) {
        if (!termData) return;
        
        console.log(`[TermAutoScroll] Auto-scrolling to term: "${termData.parent_term}"`);
        
        // Scroll in both editors
        const claimsScrolled = this.scrollToTermInClaims(termData);
        const specScrolled = this.scrollToTermInSpec(termData);
        
        if (!claimsScrolled && !specScrolled) {
            console.warn(`[TermAutoScroll] Term "${termData.parent_term}" not found in either editor`);
        }
    }
}

// Create singleton instance
export const termAutoScrollService = new TermAutoScrollService();